/**
 * Utilidad transversal para la conversión y optimización de imágenes a formato WebP.
 * Garantiza que las imágenes que ingresen al sistema no sean pesadas para el almacenamiento.
 */

export async function convertImageToWebP(
    file: File,
    maxWidth = 1600,
    quality = 0.85
): Promise<File> {
    // Si no es imagen (ej. pdf) o ya es un webp ligero menor a 500KB, no reconvertir
    if (!file.type.startsWith('image/')) {
        return file;
    }
    if (file.type === 'image/webp' && file.size < 500 * 1024) {
        return file;
    }

    return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);
            let { width, height } = img;

            // Escalar proporcionalmente si supera el ancho máximo
            if (width > maxWidth) {
                height = Math.round((height * maxWidth) / width);
                width = maxWidth;
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                resolve(file);
                return;
            }

            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        resolve(file);
                        return;
                    }
                    // Reemplazar extensión por .webp
                    const cleanName = file.name.replace(/\.[^/.]+$/, '');
                    const webpFile = new File([blob], `${cleanName}.webp`, {
                        type: 'image/webp',
                        lastModified: Date.now(),
                    });
                    resolve(webpFile);
                },
                'image/webp',
                quality
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file);
        };

        img.src = url;
    });
}
